import { Vehicle } from '..'
import { Serializable } from '../../common/types'

export const UPCOMING_LIMIT_FACTOR = 0.8

class PartNumberMaintenanceDetails {
  number: string
  maintenance_days: number
  title: string
  id: number
  life_limit_days: number
  life_limit_flight_hours: number
  maintenance_flights: number
  life_limit_flights: number
  maintenance_flight_hours: number
}

class PartInstanceMaintenanceDetails {
  part_number: PartNumberMaintenanceDetails
  maintenance_in_flight_hours: number
  days_in_use: number
  flights: number
  maintenance_in_days: number
  flights_since_last_maintenance: number
  maintenance_in_flights: number
  flight_hours: number
  last_maintenance_timestamp: string
}

export class VehicleMaintenanceDetails extends Serializable<VehicleMaintenanceDetails> {
  vehicle_id = 0
  vehicle_name = ''
  is_vehicle_in_service = true
  is_vehicle_parked = false
  unresolved_discrepancy_reports_num = 0
  part_instances: PartInstanceMaintenanceDetails[] = []
  simulation_status?: string | null
  registration_number = ''
  center_name = ''

  expiredPartsNum = 0
  upcomingExpiredPartsNum = 0
  maintenanceRequiredPartsNum = 0
  upcomingMaintenanceRequiredPartsNum = 0

  constructor(details: Partial<VehicleMaintenanceDetails>) {
    super()
    this.checkAndDeserialize(details)
    this.countPartsDetails()
  }

  private isPartExpired(partInstance: PartInstanceMaintenanceDetails, factor = 1): boolean {
    const { life_limit_days, life_limit_flights, life_limit_flight_hours } = partInstance.part_number
    const { flights, days_in_use, flight_hours } = partInstance
    return (
      (life_limit_flights !== null && flights >= life_limit_flights * factor) ||
      (life_limit_days !== null && days_in_use >= life_limit_days * factor) ||
      (life_limit_flight_hours !== null && flight_hours >= life_limit_flight_hours * factor)
    )
  }

  private doesPartRequireMaintenance = (partInstance: PartInstanceMaintenanceDetails, factor = 1): boolean => {
    const { maintenance_in_flights, maintenance_in_days, maintenance_in_flight_hours } = partInstance
    const { maintenance_flights, maintenance_days, maintenance_flight_hours } = partInstance.part_number
    const flightsSinceLastMaintenance = maintenance_flights - maintenance_in_flights
    const daysSinceLastMaintenance = maintenance_days - maintenance_in_days
    const hoursSinceLastMaintenance = maintenance_flight_hours - maintenance_in_flight_hours
    return (
      (maintenance_flights !== null && flightsSinceLastMaintenance >= maintenance_flights * factor) ||
      (maintenance_days !== null && daysSinceLastMaintenance >= maintenance_days * factor) ||
      (maintenance_flight_hours !== null && hoursSinceLastMaintenance >= maintenance_flight_hours * factor)
    )
  }

  updatePart = (part: PartInstanceMaintenanceDetails): void => {
    if (this.isPartExpired(part)) {
      this.expiredPartsNum++
    } else if (this.isPartExpired(part, UPCOMING_LIMIT_FACTOR)) {
      this.upcomingExpiredPartsNum++
    }

    if (this.doesPartRequireMaintenance(part)) {
      this.maintenanceRequiredPartsNum++
    } else if (this.doesPartRequireMaintenance(part, UPCOMING_LIMIT_FACTOR)) {
      this.upcomingMaintenanceRequiredPartsNum++
    }
  }

  countPartsDetails(): void {
    this.expiredPartsNum = 0
    this.upcomingExpiredPartsNum = 0
    this.maintenanceRequiredPartsNum = 0
    this.upcomingMaintenanceRequiredPartsNum = 0

    this.part_instances?.forEach(this.updatePart)
  }

  deserialize(details: VehicleMaintenanceDetails): void {
    this.vehicle_id = details.vehicle_id ?? 0
    this.vehicle_name = details.vehicle_name ?? ''
    this.is_vehicle_in_service = details.is_vehicle_in_service ?? true
    this.is_vehicle_parked = details.is_vehicle_parked ?? false
    this.unresolved_discrepancy_reports_num = details.unresolved_discrepancy_reports_num ?? 0
    this.part_instances = details.part_instances ?? []
    this.simulation_status = details.simulation_status
    this.registration_number = details.registration_number ?? ''
    this.center_name = details.center_name ?? ''
  }

  updateVehicle(vehicle: Vehicle): void {
    this.vehicle_name = vehicle.name ?? ''
    this.is_vehicle_in_service = vehicle.is_in_service ?? true
    this.is_vehicle_parked = vehicle.is_parked ?? false
    this.simulation_status = vehicle.simulation_status
    this.registration_number = vehicle.registration_number ?? ''
    this.center_name = vehicle.center_name ?? ''
  }
}
